import './sitemap.less'

import {m, wrap} from '@codeurs/front'
import {Component, Fragment, FunctionComponent} from 'react'
import AnimateHeight from 'react-animate-height'
import {ImageType, LinkType, PageData} from 'types'
import {t} from 'util/i18n'
import {style} from 'util/style'
import {Layout} from 'views/layout'
import {PageShare} from 'views/pages/page'
import {HeaderPortal} from 'views/partials/header'
import {Hero} from 'views/partials/hero'
import {Newsletter} from 'views/partials/newsletter'
import {Theme} from 'views/theme'

const styles = style('sitemap', ['link'])

export type SitemapItemType = {
  id: number
  channel: string
  title: string
  url: string
  image?: ImageType
  own_page?: boolean
  extra?: Array<LinkType>
  children?: Array<SitemapItemType>
}

const SitemapLink: FunctionComponent<{
  item: SitemapItemType
  link: boolean
}> = ({item, link, children}) => {
  if (link)
    return (
      <Theme.Link to={item.url} class={styles.link.is({link})}>
        {children}
      </Theme.Link>
    )
  return <div class={styles.link}>{children}</div>
}

@wrap(Layout)
export class Sitemap extends Component<{
  data: PageData & {
    sitemap: Array<SitemapItemType>
  }
}> {
  render() {
    const {url, title, hero, sitemap, newsletter, language} = this.props.data
    return (
      <Fragment>
        <div class={styles}>
          <HeaderPortal>
            <Hero hero={!!(hero && hero.image)}>
              <Theme.H1>{title}</Theme.H1>
            </Hero>
          </HeaderPortal>
          <Theme.Container class={styles('container')}>
            <div class={styles('share')}>
              <PageShare copy={title} url={url} />
            </div>
            <div class={styles('content')}>
              <SitemapContent sitemap={sitemap} />
            </div>
          </Theme.Container>
        </div>
        <Newsletter {...newsletter} language={language} />
      </Fragment>
    )
  }
}

export class SitemapContent extends Component<{
  sitemap: Array<SitemapItemType>
}> {
  state = {
    openIds: []
  }

  render() {
    const {sitemap} = this.props
    const homeKeys = ['home', 'home2050', 'homecbam']
    const sitemapTitles = sitemap
      ? sitemap
          .filter(item => homeKeys.includes(item.channel))
          .sort((a, b) =>
            homeKeys.findIndex(homeKey => homeKey === a.channel) <
            homeKeys.findIndex(homeKey => homeKey === b.channel)
              ? -1
              : 1
          )
      : []
    return sitemapTitles.map(item => (
      <Fragment>
        <Theme.H2 class={styles('site-title')}>{item.title}</Theme.H2>
        <div class={styles('site')}>
          {this.viewItem({
            ...item,
            title: t('sitemap.' + item.channel),
            children: []
          })}
          {item.children && item.children.map(child => this.viewItem(child))}
        </div>
      </Fragment>
    ))
  }

  viewItem(item: SitemapItemType) {
    const ftKey = 'ft' + item.id
    if (!this.showItem(item)) return null
    const viewChildren =
      item.children &&
      ['dictionary', 'contentpage'].indexOf(item.channel) === -1
    return (
      <div class={styles('item')}>
        <SitemapLink item={item} link={this.hasLink(item)}>
          <Theme.H3 class={styles('item-title')}>{item.title}</Theme.H3>
        </SitemapLink>
        {viewChildren && (
          <Fragment>
            {item.children.map(child => this.viewSubItem(child, ftKey))}
            {this.getExtra(item).map(child => this.viewSubItem(child, ftKey))}
          </Fragment>
        )}
      </div>
    )
  }

  viewSubItem(item: SitemapItemType, ftKey: string = '') {
    ftKey += 'ft' + item.id
    if (!this.showItem(item)) return null
    const viewChildren = this.viewChildren(item)
    const open = this.state.openIds.indexOf(ftKey) >= 0
    const toggle = (!open && viewChildren) || (open && !this.hasLink(item))
    return (
      <div class={styles('subitem').mod({open})}>
        <div class={styles('subitem-header').mod({toggle})}>
          {toggle ? (
            <div onClick={e => this.toggleItem(ftKey)}>{item.title}</div>
          ) : (
            <SitemapLink item={item} link={this.hasLink(item)}>
              <div class={styles('subitem-header-link')}>{item.title}</div>
            </SitemapLink>
          )}
          {viewChildren && (
            <div
              onClick={() => this.toggleItem(ftKey)}
              class={styles('subitem-header-toggle')}
            >
              <div
                class={styles('subitem-header-toggle-container').mod({open})}
              />
            </div>
          )}
        </div>
        {viewChildren && (
          <AnimateHeight duration={300} height={open ? 'auto' : 0}>
            <div class={styles('subitem-children')}>
              <Fragment>
                {item.children.map(child => this.viewSubItem(child, ftKey))}
                {this.getExtra(item).map(child =>
                  this.viewSubItem(child, ftKey)
                )}
              </Fragment>
            </div>
          </AnimateHeight>
        )}
      </div>
    )
  }

  toggleItem(id) {
    let currentIds = [...this.state.openIds]
    if (currentIds.indexOf(id) >= 0)
      currentIds = currentIds.filter(item => item !== id)
    else currentIds.push(id)
    this.setState({openIds: currentIds})
  }

  hasLink(item: SitemapItemType) {
    switch (item.channel) {
      case 'category':
      case 'library':
      case 'search':
      case 'term':
      case 'tags':
        return false
      case 'document':
      case 'link_item':
      case 'media_item':
      case 'presentation':
      case 'subcategory':
        return item.own_page
    }
    return true
  }

  showItem(item: SitemapItemType) {
    if (this.hasLink(item)) return true
    switch (item.channel) {
      case 'search':
      case 'term':
      case 'document':
      case 'link_item':
      case 'media_item':
      case 'presentation':
      case 'tags':
        return false
    }
    return true
  }

  viewChildren(item: SitemapItemType) {
    if (!item.children) return false
    if (!item.children.length) return false
    if (['dictionary'].indexOf(item.channel) >= 0) return false
    for (const child of item.children) {
      if (this.showItem(child)) return true
    }
    return false
  }

  getExtra(item: SitemapItemType) {
    const {sitemap} = this.props
    const extraItems = []
    if (!item.extra) return extraItems
    for (const extraItem of item.extra) {
      if (extraItem.type !== 'entry') continue
      const foundItem = this.findExtra(extraItem.id, sitemap)
      if (foundItem) extraItems.push(foundItem)
    }
    return extraItems
  }

  findExtra(id: number, map: Array<SitemapItemType>) {
    for (const item of map) {
      if (id === item.id) return item
      if (!item.children) continue
      if (!item.children.length) continue
      const subitem = this.findExtra(id, item.children)
      if (subitem) return subitem
    }
    return false
  }
}
